import type { PropsWithChildren } from 'react';
import { useMemo } from 'react';

import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, ThemeProvider } from '@mui/material/styles';

import type { ThemeLocalization } from '../locale/mui-locale-provider/mui-locale-provider';
import { MODE_KEY } from '../theme-mode/theme-mode';
import type { PaletteMode } from '../theme-mode/theme-mode';
import { ThemeModeProvider } from '../theme-mode/theme-mode-provider';

import { themeBase } from './theme-base';

import type {} from '@mui/lab/themeAugmentation';
import type {} from '@mui/material/themeCssVarsAugmentation';
import type {} from '@mui/x-data-grid-pro/themeAugmentation';
import type {} from '@mui/x-date-pickers-pro/themeAugmentation';

export interface FenrirThemeProviderProps {
  /**
   * `light` | `dark`
   */
  defaultMode: PaletteMode;
  localization: ThemeLocalization;
}

export const FenrirThemeProvider = ({
  defaultMode,
  children,
  localization,
  ...rest
}: PropsWithChildren<FenrirThemeProviderProps>) => {
  const themeWithLocale = useMemo(
    () =>
      createTheme(
        themeBase,
        localization.datePicker,
        localization.dataGrid,
        localization.core,
      ),
    [localization],
  );

  return (
    <ThemeProvider
      theme={themeWithLocale}
      defaultMode={defaultMode}
      modeStorageKey={MODE_KEY}
      disableTransitionOnChange
      // optimize the performance and prevent the dark mode flickering when users refresh the page
      noSsr
      {...rest}
    >
      <CssBaseline enableColorScheme />
      <ThemeModeProvider defaultMode={defaultMode}>
        {children}
      </ThemeModeProvider>
    </ThemeProvider>
  );
};
